// Hosted-only billing helpers (docs/09 §4 billing screens). Credit billing is
// loaded only on hosted installs where Stripe is wired; self-managed installs
// report `credit_billing_enabled: false` on GET /v1/me and the billing screens
// "do not exist" there. Accounting is carried in integer credits; Stripe
// Checkout owns charges; the API catalog supplies informational picker prices.

import { useQuery } from "@tanstack/react-query";

import type { components } from "../api/schema";
import { apiClient } from "./apiClient";
import { ApiRequestError } from "./errors";
import { queryKeys } from "./queryKeys";

export type BillingEntitlement =
  components["schemas"]["BillingEntitlementResponse"];

export interface CreditBilling {
  // True only on hosted installs where Stripe billing is loaded.
  enabled: boolean;
  stripePublishableKey: string | null;
  isError: boolean;
  isPending: boolean;
}

async function getMe() {
  const { data, error } = await apiClient.GET("/v1/me");
  if (error) {
    throw new ApiRequestError(error);
  }
  return data;
}

// Hosted gate read from GET /v1/me. The billing screens self-gate on this and
// the settings nav / command palette only surface the Billing row when enabled.
export function useCreditBilling(): CreditBilling {
  const me = useQuery({
    queryKey: queryKeys.me,
    queryFn: getMe,
  });

  return {
    enabled: me.data?.credit_billing_enabled === true,
    stripePublishableKey: me.data?.stripe_publishable_key ?? null,
    isError: me.isError,
    isPending: me.isPending,
  };
}

export async function getBillingEntitlement(): Promise<BillingEntitlement> {
  const { data, error } = await apiClient.GET("/v1/billing/entitlement");
  if (error) {
    throw new ApiRequestError(error);
  }
  return data;
}

export type UsageBasedPricingUpdate =
  components["schemas"]["UsageBasedPricingUpdateRequest"];

// PATCH /v1/billing/usage-based (owner-gated). Opts the org in/out of
// usage-based overage pricing and sets the required hard spend cap. The server
// re-validates `plan_allotment <= cap <= 2 x plan_allotment` when enabling and
// returns the refreshed entitlement projection.
export async function updateUsageBasedPricing(
  body: UsageBasedPricingUpdate,
): Promise<BillingEntitlement> {
  const { data, error } = await apiClient.PATCH("/v1/billing/usage-based", {
    body,
  });
  if (error) {
    throw new ApiRequestError(error);
  }
  return data;
}

// The spend cap must sit between 1x and 2x the monthly plan allotment. Returns
// the inclusive [min, max] bounds, or null when there is no settled allotment
// (usage-based pricing cannot be enabled until the plan is active).
export function usageSpendCapBounds(
  creditAllotment: number | null | undefined,
): { min: number; max: number } | null {
  if (creditAllotment == null || creditAllotment <= 0) {
    return null;
  }
  return { min: creditAllotment, max: creditAllotment * 2 };
}

// A plan key is an opaque Stripe-side identifier (e.g. `team_monthly`); the
// console never hardcodes plan names, so it humanizes the key for a label.
export function humanizePlanKey(planKey: string): string {
  const words = planKey
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) {
    return planKey;
  }
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function formatCredits(credits: number): string {
  return credits.toLocaleString();
}

// The entitlement projection, rather than activity counters, owns the credit
// threshold. Keep the sealed 80% / 100% states shared by billing surfaces.
export function creditUsageState(
  creditBalance: number,
  creditAllotment: number | null | undefined,
): "warn_80" | "exhausted" | null {
  if (creditAllotment == null || creditAllotment <= 0) {
    return null;
  }
  const used = Math.max(creditAllotment - creditBalance, 0);
  const percentage = used / creditAllotment;
  if (percentage >= 1) {
    return "exhausted";
  }
  return percentage >= 0.8 ? "warn_80" : null;
}
